import { z } from 'zod';
import { PointSchema } from './geojson';

/**
 * Staging areas (Phase R.4.2) — the Logistics "staging area" object the locked
 * IMS Resource Status Lifecycle's `staging` state attaches to (operator-cop-
 * doctrine §2.5 "manages staging areas", §6.4 decision (a), §9.1 ICS-209 block
 * 49 Resources committed). A forward operating base / receiving site declared on
 * an incident; resource_requests bind to one when they transition to `staging`.
 *
 * Soft-deleted (closed_at) on demobilization rather than hard-deleted, so the
 * SITREP + audit trail keep referencing a closed area.
 */

const UUID = z.string().uuid();

/* ------------------------------------------------------------------ *
 * Create — POST /api/staging-areas
 * ------------------------------------------------------------------ */

export const StagingAreaCreateSchema = z.object({
  session_id: z.string().min(1).max(128),
  incident_id: UUID,
  name: z.string().min(1).max(200),
  /** [lon, lat] — the staging area's point. */
  lon: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
  capacity_assets: z.number().int().nonnegative().max(100000).optional(),
  capacity_personnel: z.number().int().nonnegative().max(1000000).optional(),
  notes: z.string().max(2000).optional(),
});
export type StagingAreaCreate = z.infer<typeof StagingAreaCreateSchema>;

/** PATCH /api/staging-areas/:id/close — soft-close (sets closed_at). */
export const StagingAreaCloseSchema = z.object({
  session_id: z.string().min(1).max(128),
});
export type StagingAreaClose = z.infer<typeof StagingAreaCloseSchema>;

/* ------------------------------------------------------------------ *
 * Read shapes
 * ------------------------------------------------------------------ */

/** The full staging area, serialized for the wire. */
export const StagingAreaSchema = z.object({
  id: z.string(),
  incident_id: z.string(),
  incident_number: z.string().nullable(),
  name: z.string(),
  location: PointSchema.nullable(),
  capacity_assets: z.number().int().nullable(),
  capacity_personnel: z.number().int().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  closed_at: z.string().nullable(),
  /** Count of resource_requests currently bound to this area (detail only). */
  assigned_request_count: z.number().int().optional(),
});
export type StagingArea = z.infer<typeof StagingAreaSchema>;

export const StagingAreaListResponseSchema = z.object({
  staging_areas: z.array(StagingAreaSchema),
});
export type StagingAreaListResponse = z.infer<typeof StagingAreaListResponseSchema>;
